import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const prefill = params.get("email");
    if (prefill) setEmail(prefill);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    const res = await register(name, email, password);
    setLoading(false);
    if (res.ok) {
      const u = res.user || {};
      // Send the new account to whichever program they just unlocked.
      let next = "/dashboard";
      if (u.has_mens_guide_access) next = "/dashboard/mens-guide";
      else if (u.has_menopause_invitation_access) next = "/dashboard/menopause";
      navigate(next, { replace: true });
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-register">
      <div className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-cream to-sage-soft relative">
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="text-ink max-w-md">
            <div className="eyebrow mb-6 text-sage">Create your account</div>
            <div className="font-serif-display text-5xl leading-[1.05] text-ink">
              A quiet place just for you.
            </div>
            <p className="mt-6 text-ink-soft leading-relaxed">
              Your Rewired Life content, meditations, and workbooks — all kept safely, accessed on your time.
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif-display text-2xl text-ink" data-testid="register-logo">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>
          <h1 className="font-serif-display text-4xl text-ink mt-10 leading-tight">Create your account</h1>
          <p className="mt-3 text-ink-soft">
            Use the same email you used at checkout to unlock your access automatically.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
            <div>
              <label className="eyebrow block mb-2 text-ink-soft">First Name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2 text-ink-soft">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2 text-ink-soft">Password</label>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                data-testid="register-password"
              />
              <p className="text-xs text-ink-soft mt-2">At least 8 characters.</p>
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="register-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? "Creating account…" : "Create account"}
              {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
            </button>
          </form>

          <p className="mt-8 text-sm text-ink-soft">
            Already have one?{" "}
            <Link to="/login" className="text-sage hover:text-sage-dark font-medium" data-testid="register-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
